import React, { useEffect, useState, useCallback } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { Paper } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { accepted, fetchEmployeeDevice } from '../../services/InventoryService'
import DeviceDetailsModal from './DeviceDetailsModal'
import TermAndConditionModal from './TermAndConditionModal'
import {
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel
} from '@mui/material'

const DEFAULT_PAGE_SIZE = 10

const STATUS_STYLES = {
    ASSIGNED: 'bg-green-100 text-green-700',
    PENDING: 'bg-yellow-100 text-yellow-700',
    RETURNED: 'bg-red-100 text-red-700'
}

const ItemEmployeeAssignment = () => {
    const navigate = useNavigate()
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [totalCount, setTotalCount] = useState(0)
    const [searchInput, setSearchInput] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [refresh, setRefresh] = useState(false)
    const [openDeviceModal, setOpenDeviceModal] = useState(false)
    // const [selectedDevice, setSelectedDevice] = useState(null)
    const [openAcceptanceModal, setAcceptanceModal] = useState(false)
    const [selectedRows, setSelectedRows] = useState([])
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
    const [confirmChecked, setConfirmChecked] = useState(false)
    const [snackbar, setSnackbar] = React.useState({
        open: false,
        message: '',
        severity: 'success'
    })
    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearchTerm(searchInput)
            setPage(1)
        }, 400)

        return () => clearTimeout(timeout)
    }, [searchInput])

    const fetchAssignments = useCallback(async () => {
        setLoading(true)
        try {
            const filter = {
                page,
                limit: pageSize,
                search: searchTerm
            }

            const response = await fetchEmployeeDevice(filter)
            const data = response?.data || []

            setTotalCount(response?.count || 0)

            const formattedRows = data.map((record) => ({
                id: record.id,
                item: `${record.item.category.toUpperCase()} - ${record.item.brand.toUpperCase()} ${record.item.model.toUpperCase()}`,
                brand: record.item.brand.toUpperCase(),
                model: record.item.model.toUpperCase(),
                category: record.item.category.toUpperCase(),
                serialNumber: record.item.serialNumber || 'N/A',
                assignedTo: record.employee
                    ? `${record.employee.firstName.toUpperCase()} ${record.employee.lastName.toUpperCase()}`
                    : record.location?.toUpperCase() || 'N/A',
                assignedDate: record.assignedDate
                    ? new Intl.DateTimeFormat('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit'
                      }).format(new Date(record.assignedDate))
                    : '—',
                returnedDate: record.returnedDate
                    ? new Intl.DateTimeFormat('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit'
                      }).format(new Date(record.returnedDate))
                    : '—',
                status: record.status.toUpperCase(),
                isTermsAccepted: record.isTermsAccepted,
                termsAcceptedAt: record.termsAcceptedAt
                    ? new Intl.DateTimeFormat('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit'
                      }).format(new Date(record.termsAcceptedAt))
                    : '—',

                itemDetails: record.item
            }))

            setRows(formattedRows)
        } catch (error) {
            console.error('Error fetching assignments:', error)
        } finally {
            setLoading(false)
        }
    }, [page, pageSize, searchTerm])

    useEffect(() => {
        fetchAssignments()
    }, [fetchAssignments, refresh])

    const columns = [
        {
            field: 'item',
            headerName: 'Item',
            flex: 1,
            renderCell: (params) => (
                <span title={params.value} className="truncate">
                    {params.value}
                </span>
            )
        },
        {
            field: 'serialNumber',
            headerName: 'Serial No.',
            flex: 1,
            renderCell: (params) => (
                <span className={params.value === 'N/A' ? 'text-gray-400 italic' : ''}>{params.value}</span>
            )
        },

        {
            field: 'assignedDate',
            headerName: 'Assigned Date',
            flex: 1
        },
        {
            field: 'returnedDate',
            headerName: 'Returned Date',
            flex: 1
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[params.value] || ''}`}>
                    {params.value}
                </span>
            )
        }
    ]

    const handleBulkAccept = async () => {
        if (selectedRows.length === 0) {
            setSnackbar({
                open: true,
                message: 'Please select at least one item.',
                severity: 'warning'
            })
            return
        }

        try {
            await Promise.all(selectedRows.map((id) => accepted({ token: id })))

            setSnackbar({
                open: true,
                message: 'Selected items accepted successfully.',
                severity: 'success'
            })

            setSelectedRows([])
            setRefresh((prev) => !prev)
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Bulk accept failed.',
                severity: 'error'
            })
        } finally {
            setOpenConfirmDialog(false)
            setConfirmChecked(false)
        }
    }

    const handleAcceptTerms = async (deviceId) => {
        try {
            const response = await accepted({ token: deviceId })
            console.log('Terms accepted response:', response)

            setSnackbar({
                open: true,
                message: response.message || 'Terms and Conditions accepted successfully.',
                severity: 'success'
            })

            setRefresh((prev) => !prev)
        } catch (error) {
            console.error('Failed to accept terms', error)

            setSnackbar({
                open: true,
                message: 'Failed to accept Terms and Conditions. Please try again.',
                severity: 'error'
            })
        } finally {
            setAcceptanceModal(false)
        }
    }

    return (
        <div className="flex flex-col p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-bold text-black">Employee Device Assignments</h1>
                </div>

                <div>
                    <button
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setAcceptanceModal(true)
                        }}
                        className="text-xl font-bold text-blue-600 hover:underline"
                    >
                        Corporate Device and Peripheral Care Policy
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search by item, serial number, or employee"
                    className="p-2 border rounded w-full"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
            </div>

            {/* Count */}
            <p className="text-sm text-gray-500 mb-2">
                Showing {rows.length} of {totalCount} assignments
            </p>

            <div className="flex justify-start mb-6">
                <button
                    onClick={() => setOpenConfirmDialog(true)}
                    className="bg-red-600 text-white text-sm px-3 py-1.5 rounded hover:bg-red-700 transition"
                >
                    Accept Selected
                </button>
            </div>

            {/* Table */}
            <Paper sx={{ width: '100%', height: 'calc(100vh - 280px)' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalCount}
                    pageSizeOptions={[5, 10, 25]}
                    page={page - 1}
                    pageSize={pageSize}
                    checkboxSelection
                    disableRowSelectionOnClick
                    isRowSelectable={(params) => params.row.status === 'PENDING'}
                    onRowSelectionModelChange={(ids) => {
                        setSelectedRows(ids)
                    }}
                    onPaginationModelChange={({ page, pageSize }) => {
                        setPage(page + 1)
                        setPageSize(pageSize)
                    }}
                    sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#fafafa',
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            backgroundColor: '#fcfcfc'
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#f5f5f5'
                        },
                        '& .MuiDataGrid-cell': {
                            outline: 'none'
                        }
                    }}
                />
            </Paper>

            <DeviceDetailsModal
                open={openDeviceModal}
                onClose={() => setOpenDeviceModal(false)}
                // device={selectedDevice}
            />

            <TermAndConditionModal
                open={openAcceptanceModal}
                onClose={() => setAcceptanceModal(false)}
                // device={selectedDevice}
                onAcceptTerms={handleAcceptTerms}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
                <DialogTitle>Accept Confirmation</DialogTitle>

                <DialogContent>
                    <p className="text-text  mb-3">Are you sure you want to accept the selected device assignments?</p>

                    <FormControlLabel
                        control={
                            <Checkbox checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} />
                        }
                        label="I have read and accept the Corporate Device and Peripheral Care Policy."
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenConfirmDialog(false)} color="inherit">
                        Cancel
                    </Button>

                    <Button onClick={handleBulkAccept} disabled={!confirmChecked} variant="contained" color="success">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default ItemEmployeeAssignment
